import { inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { environment } from '../../../environments/environment';

export interface CookieConsent {
  necessary: true;
  performance: boolean;
  advertising: boolean;
}

const COOKIE_NAME = 'pyg_cookie_consent';
const COOKIE_EXPIRY_MONTHS = 6;

@Injectable({
  providedIn: 'root'
})
export class CookieConsentService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly _consent$: BehaviorSubject<CookieConsent | null>;

  readonly consent$: Observable<CookieConsent | null>;
  readonly settingsOpen = signal(false);

  openSettings(): void {
    this.settingsOpen.set(true);
  }

  constructor(private gaService: GoogleAnalyticsService) {
    this._consent$ = new BehaviorSubject<CookieConsent | null>(this.loadConsent());
    this.consent$ = this._consent$.asObservable();
  }

  get hasConsented(): boolean {
    return this._consent$.value !== null;
  }

  get consent(): CookieConsent | null {
    return this._consent$.value;
  }

  acceptAll(): void {
    this.save({ necessary: true, performance: true, advertising: true });
  }

  acceptNecessaryOnly(): void {
    this.save({ necessary: true, performance: false, advertising: false });
  }

  save(consent: CookieConsent): void {
    const previous = this._consent$.value;
    this.setCookie(COOKIE_NAME, JSON.stringify(consent), COOKIE_EXPIRY_MONTHS);
    this._consent$.next(consent);
    this.applyToTrackers(consent);
    const trackersChanged =
      !previous ||
      previous.performance !== consent.performance ||
      previous.advertising !== consent.advertising;
    if (trackersChanged && this.isBrowser) {
      window.location.reload();
    }
  }

  private loadConsent(): CookieConsent | null {
    const stored = this.getCookie(COOKIE_NAME);
    if (!stored) return null;
    try {
      const parsed: unknown = JSON.parse(stored);
      if (!this.isValidConsent(parsed)) return null;
      this.applyToTrackers(parsed);
      return parsed;
    } catch {
      return null;
    }
  }

  private isValidConsent(obj: unknown): obj is CookieConsent {
    if (typeof obj !== 'object' || obj === null) return false;
    const c = obj as Record<string, unknown>;
    return c['necessary'] === true &&
      typeof c['performance'] === 'boolean' &&
      typeof c['advertising'] === 'boolean';
  }

  private applyToTrackers(consent: CookieConsent): void {
    this.updateGaConsent(consent);
    if (environment.production && consent.performance) {
      this.loadHotjarIfNeeded();
    }
  }

  private updateGaConsent(consent: CookieConsent): void {
    this.gaService.gtag('consent', 'update', {
      analytics_storage: consent.performance ? 'granted' : 'denied',
      ad_storage: consent.advertising ? 'granted' : 'denied',
      ad_user_data: consent.advertising ? 'granted' : 'denied',
      ad_personalization: consent.advertising ? 'granted' : 'denied'
    });
  }

  private loadHotjarIfNeeded(): void {
    if (!this.isBrowser || (window as any)._hjSettings) return;
    const script = document.createElement('script');
    script.src = 'assets/js/hotjar.js';
    document.head.appendChild(script);
  }

  private setCookie(name: string, value: string, expiryMonths: number): void {
    if (!this.isBrowser) return;
    const date = new Date();
    date.setFullYear(date.getFullYear(), date.getMonth() + expiryMonths, date.getDate());
    const expires = `expires=${date.toUTCString()}`;
    document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/;SameSite=Lax`;
  }

  private getCookie(name: string): string | null {
    if (!this.isBrowser) return null;
    const nameEQ = `${name}=`;
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(nameEQ)) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    return null;
  }
}
